import { useCallback } from 'react';
import { useHistory } from 'react-router';
import { FlowRouteType } from '~/utils/flow.utils';
import { IFlowService } from '~/types/angular.types';
import { goToRoute as goToRouteCB, useGoToRoute } from '~/utils/url.utils';
import { IFlow } from '~/types/model.types';
import type { BuilderUiStore } from '~/stores/builder.ui.store';
import { isEmpty } from 'lodash';
import { getPageRoute } from '~/utils/client-experience.utils';
import { TBuilderQueryParams } from '~/types/builder.types';
import { useAuthenticatedGlobalContext } from '~/stores/useAuthenticatedGlobalContext';
import { isReactFirst } from '~/utils/environment.utils';
import { trackError } from '~/services/logger/error.utils';
import { User } from '~/models/user.model';
import useAnalytics from '~/common/useAnalytics';
import { useAngularFlowService } from '~/common/useAngularFlowService';
import useNavigation from '~/apps/main/App/hooks/useNavigation';
import useAppStates from '~/apps/main/App/hooks/useAppStates';
import { getFlowUrl } from '~/components/Builder/Common/flow-url.utils';
import useCXTabAuthentication from '~/components/Builder/BuilderNavBar/useCXTabAuthentication';

export type TGoToFlow = {
    forceRoute?: FlowRouteType;
    openOnNewTab?: boolean;
    queryParams?: TBuilderQueryParams;
    isReplace?: boolean;
    useAngularNavigation?: boolean;
    flowId: string;
};

// This function should only be used in places where you can't use the hooks, these places are global modals because
// of wrappers issues and mobile api adapter
export const goToFlow = ({
    flowId,
    forceRoute,
    openOnNewTab,
    queryParams,
    isReplace,
    FlowService,
    useAngularNavigation,
    history,
    navigate,
    navigateReplace,
    AppStates
}: TGoToFlow & {
    history?: ReturnType<typeof useHistory>;
    FlowService?: IFlowService;
    navigate?: ReturnType<typeof useNavigation>['navigate'];
    navigateReplace?: ReturnType<typeof useNavigation>['navigateReplace'];
    AppStates?: any;
}) => {
    const route = getFlowUrl({
        flowId,
        forceRoute,
        queryParams,
        openInANewTab: openOnNewTab
    });

    if (openOnNewTab) {
        window.open(route, '_blank');
    } else if (useAngularNavigation) {
        if (isReactFirst()) {
            // Ensure navigate and AppStates are available before using them
            if (!navigate || !AppStates) {
                trackError({
                    message:
                        '[REACT_FIRST] goToFlow called in React First mode without navigate or AppStates',
                    error: new Error()
                });
                return;
            }

            const params: Record<string, any> = {
                flow_id: flowId
                // TODO: How to get goBackTo details? The original code used $state.
                // For now, omitting goBackTo logic.
                // goBackTo: { name: ???, params: ??? }
            };
            let targetRoute = AppStates.root_core_oneFlow; // Default route

            // Map forceRoute (target in Angular code) to appropriate route and params
            switch (forceRoute) {
                case FlowRouteType.edit:
                    params.action = 'edit';
                    break;

                case FlowRouteType.responses:
                    params.action = 'responses';
                    // params.goBackTo = goBackTo; // Cannot get goBackTo here
                    break;

                case FlowRouteType.preview:
                    params.action = 'edit';
                    targetRoute = AppStates.root_core_oneFlowPreview;
                    break;

                default: // No forceRoute or unknown
                    break;
            }

            const navigateOptions = {
                route: targetRoute,
                params,
                stateParams: queryParams
            };

            return isReplace
                ? navigateReplace(navigateOptions)
                : navigate(navigateOptions);
        } else {
            // goToFlow calls that from outside the Router, are using
            // FlowService from angular as a routing service. This
            // line keeps this logics
            FlowService.goToFlow(flowId, forceRoute, {}, queryParams);
        }
    } else {
        goToRouteCB({ route, history, isReplace, stateParams: queryParams });
    }
};

export function useFlowNavigation() {
    const { navigate, navigateReplace } = useNavigation();
    const history = useHistory();
    const { goToRoute } = useGoToRoute();
    const FlowService = useAngularFlowService();
    const AppStates = useAppStates();
    const {
        userStore: { currUser },
        companyStore: { currCompany }
    } = useAuthenticatedGlobalContext();
    const openCXWithAuth = useCXTabAuthentication();
    const { track } = useAnalytics();

    const goToFlowCB = useCallback(
        (props: TGoToFlow) =>
            goToFlow({
                history,
                FlowService,
                navigate,
                navigateReplace,
                AppStates,
                ...props
            }),
        [FlowService, history, navigate, navigateReplace, AppStates]
    );

    const goToResponses = useCallback(
        ({ flowId, isReplace }: Pick<TGoToFlow, 'isReplace' | 'flowId'>) => {
            goToFlowCB({
                flowId: flowId,
                forceRoute: FlowRouteType.responses,
                isReplace
            });
        },
        [goToFlowCB]
    );

    const goToFlowByUser = useCallback(
        ({ flow, user }: { flow?: IFlow; user: User }) => {
            if (!flow) {
                return;
            }

            const isClientInFlow = () => {
                if (!flow || user.isClient()) {
                    return true;
                }

                return user.company && flow.company_id !== user.company._id;
            };
            const companies = user.companies || [user.company];

            if (
                !isClientInFlow() &&
                companies.some(c => c._id === flow.company_id)
            ) {
                track('action: flow feed item', {
                    status: flow.status
                });

                goToFlowCB({ flowId: flow._id });
            } else {
                track('action: client flow action required', {
                    status: flow.status
                });

                openCXWithAuth(flow.flow_url);
            }
        },
        [track, goToFlowCB, openCXWithAuth]
    );

    const goToFlowSimplified = useCallback(
        ({
            flow,
            isPreview,
            isConfigure,
            stateParams
        }: {
            flow: IFlow;
            isPreview?: boolean;
            isConfigure?: boolean;
            stateParams?: Record<string, string>;
        }) => {
            const userCompanies = isEmpty(currUser.companies)
                ? [currCompany]
                : currUser.companies;

            if (
                isReactFirst() &&
                (currUser?.isClient() ||
                    !userCompanies.some(
                        company => company._id === flow.company_id
                    ))
            ) {
                return goToFlowByUser({ flow, user: currUser });
            }

            // TODO: To be removed when fully moved to react first
            if (
                FlowService &&
                (currUser?.isClient() ||
                    !userCompanies.some(
                        company => company._id === flow.company_id
                    ))
            ) {
                return FlowService.goToFlowByUser(flow, currUser);
            }

            return goToFlow({
                flowId: flow._id,
                history,
                FlowService,
                navigate,
                navigateReplace,
                AppStates,
                useAngularNavigation: !!FlowService,
                ...(isPreview ? { forceRoute: FlowRouteType.preview } : {}),
                queryParams: isConfigure
                    ? { openConfigureModal: 'true', ...stateParams }
                    : stateParams
            });
        },
        [
            currUser,
            currCompany,
            FlowService,
            history,
            navigate,
            navigateReplace,
            AppStates,
            goToFlowByUser
        ]
    );

    // This should be implemented through url and not just by selecting the page
    const goToFlowPage = useCallback(
        ({
            pageId,
            builderUiStore
        }: {
            pageId: string;
            builderUiStore: BuilderUiStore;
        }) => {
            const { selectedFlowId } = builderUiStore;
            goToFlowCB({
                flowId: selectedFlowId,
                forceRoute: FlowRouteType.edit
            });

            builderUiStore.selectPage(pageId);
        },
        [goToFlowCB]
    );

    const goToFlowPreview = useCallback(
        ({
            flowId,
            pageIndex,
            pageTitle,
            isActiveRevision
        }: {
            flowId: string;
            pageIndex: number;
            pageTitle: string;
            isActiveRevision?: boolean;
        }) => {
            const selectedPageRoute = getPageRoute(pageIndex, pageTitle);

            let route = `/flow/${flowId}/edit/preview/${selectedPageRoute}`;
            if (isActiveRevision) {
                route += '?activeMode=true';
            }

            goToRoute({ route });
        },
        [goToRoute]
    );

    const exitFlowPreview = useCallback(
        ({
            backToRoute,
            isMweb,
            builderUiStore,
            isOpenConfigurationModal,
            lastPageIdOnPreview
        }: {
            backToRoute?: string;
            isMweb?: boolean;
            builderUiStore: BuilderUiStore;
            isOpenConfigurationModal?: boolean;
            lastPageIdOnPreview?: string;
        }) => {
            const { selectedFlow, selectPage, setIsConfigureModalOpen } =
                builderUiStore;

            // This means user navigated directly to preview url
            // In this case, take him/her back to the builder
            if (!backToRoute && isMweb) {
                if (selectedFlow.isTemplate) {
                    navigate({
                        route: AppStates.root_core_navigation_flows_template_gallery
                    });

                    return;
                }

                navigate({
                    route: AppStates.root_core_navigation_event_workspace_files,
                    params: {
                        workspace_id: selectedFlow?.workspace_id,
                        event_id: selectedFlow?.project_id
                    }
                });

                return;
            }

            if (lastPageIdOnPreview) {
                selectPage(lastPageIdOnPreview);
            }
            if (isOpenConfigurationModal) {
                setIsConfigureModalOpen(true);
            }

            goToRouteCB({
                route: backToRoute,
                history
            });
        },
        [
            AppStates.root_core_navigation_event_workspace_files,
            AppStates.root_core_navigation_flows_template_gallery,
            history,
            navigate
        ]
    );

    return {
        goToFlow: goToFlowCB,
        goToFlowSimplified,
        goToResponses,
        goToFlowPage,
        goToFlowPreview,
        exitFlowPreview,
        goToFlowByUser
    };
}

export const useGoToFlowByUser = () => {
    const FlowService = useAngularFlowService();
    const reactFlowService = useFlowNavigation();

    const goToFlowByUser = ({ flow, user }: { flow: IFlow; user: User }) => {
        if (isReactFirst()) {
            reactFlowService.goToFlowByUser({ flow, user });
            return;
        }

        FlowService.goToFlowByUser(flow, user);
    };

    return { goToFlowByUser };
};
